import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Divider, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { AppDispatch } from '../../reduxStore/Store';
import { createReportJob, clearMessages } from '../../reduxStore/ReportJobSlice';
import { TypeRapport, FormatRapport } from '../../constants/enums';
import SelectReportTemplate from '../Selects/SelectReportTemplate';
import DocumentVenteFilter from '../Filters/DocumentVenteFilter';
import StockFilters from '../Filters/StockFilters';
import ClientAutoC from '../AutoComplete/ClientAutoC';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import EmployeAutoC from '../AutoComplete/EmployeAutoC';
import SelectCaisse from '../Selects/SelectCaisse';
import { useSnackbar } from '../SnackbarProvider';
import { Tiers, GeneriqueData } from '../../models/models';
import { labels } from '../../constants/labels';

interface Props {
  onCancel: () => void;
  /** Pré-sélection du type de rapport (optionnel, ex: depuis Comptes.tsx) */
  initialTypeRapport?: string;
  /** Paramètres pré-remplis (optionnel) */
  initialParametres?: Record<string, any>;
}

const TYPE_LABELS: Record<string, string> = {
  DOCUMENT_VENTE:     'Documents de vente',
  LISTE_ARTICLE:      'Liste des articles',
  STOCK:              'État du stock',
  TIERS:              'Liste des tiers',
  COMPTE_CLIENT:      `Compte ${labels.client}`,
  COMPTE_FOURNISSEUR: `Compte ${labels.fournisseur}`,
  COMPTE_EMPLOYE:     `Compte ${labels.employe}`,
  COMPTE_CAISSE:      'État de caisse',
  LISTE_REGLEMENT:    'Liste des règlements',
  BALANCE_EMPLOYE:    'Balance employés',
};

const COMPTE_TYPES = [TypeRapport.COMPTE_CLIENT, TypeRapport.COMPTE_FOURNISSEUR, TypeRapport.COMPTE_EMPLOYE, TypeRapport.COMPTE_CAISSE];

const ReportJobForm: React.FC<Props> = ({ onCancel, initialTypeRapport, initialParametres }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();

  const docVenteFilters     = useSelector((state: any) => state.DocumentVenteSlice.filters);
  const stockFilters        = useSelector((state: any) => state.StockSlice.filters);
  const selectedDepartement = useSelector((state: any) => state.AppSlice.selectedDepartement);

  const [typeRapport, setTypeRapport] = useState<string>(initialTypeRapport ?? TypeRapport.DOCUMENT_VENTE);
  const [format,      setFormat]      = useState<string>(FormatRapport.EXCEL);
  const [templateId,  setTemplateId]  = useState<number | null>(null);
  const [dateDebut,   setDateDebut]   = useState(initialParametres?.startDate ?? '');
  const [dateFin,     setDateFin]     = useState(initialParametres?.endDate   ?? '');

  // Filtres compte tiers
  const [selectedTiers,  setSelectedTiers]  = useState<Tiers | null>(null);
  const [selectedCaisse, setSelectedCaisse] = useState<GeneriqueData | null>(null);

  const isDocVente  = typeRapport === TypeRapport.DOCUMENT_VENTE;
  const isStock     = typeRapport === TypeRapport.STOCK;
  const isCompte    = COMPTE_TYPES.includes(typeRapport as TypeRapport);
  const hasCustomFilter = isDocVente || isStock || isCompte;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let parametres: Record<string, any> = {};

    if (isDocVente) {
      parametres = { ...docVenteFilters };
    } else if (isStock) {
      parametres = { ...stockFilters };
    } else if (isCompte) {
      if (typeRapport === TypeRapport.COMPTE_CAISSE) {
        if (selectedCaisse) parametres.caisse_id = selectedCaisse.id;
      } else {
        if (selectedTiers) parametres.tiers_id = selectedTiers.id;
      }
      if (dateDebut) parametres.startDate = dateDebut;
      if (dateFin)   parametres.endDate   = dateFin;
    } else {
      if (dateDebut) parametres.date_debut = dateDebut;
      if (dateFin)   parametres.date_fin   = dateFin;
    }

    // Retirer limit/offset — ils appartiennent à la pagination, pas au rapport
    const { limit, offset, ...filtresClean } = parametres as any;

    const result = await dispatch(createReportJob({
      type_rapport:   typeRapport,
      format:         format as 'PDF' | 'EXCEL',
      template_id:    templateId,
      departement_id: selectedDepartement?.id ?? null,
      parametres:     Object.keys(filtresClean).length ? filtresClean : null,
    }));

    if (createReportJob.fulfilled.match(result)) {
      showSnackbar(`Rapport lancé — job #${result.payload.job_id}`, 'success');
      dispatch(clearMessages());
      onCancel();
    } else {
      showSnackbar(result.payload as string, 'error');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold">Nouveau rapport</Typography>
        </Grid>

        {/* Type / Format / Template */}
        <Grid item xs={12} sm={5}>
          <TextField
            select label="Type de rapport" value={typeRapport}
            onChange={e => { setTypeRapport(e.target.value); setSelectedTiers(null); setSelectedCaisse(null); }}
            fullWidth required size="small"
          >
            {Object.values(TypeRapport)
              .filter(t => t !== TypeRapport.IMPRESSION_DOCUMENT)
              .map(t => (
                <MenuItem key={t} value={t}>{TYPE_LABELS[t] ?? t}</MenuItem>
              ))}
          </TextField>
        </Grid>

        <Grid item xs={6} sm={3}>
          <TextField
            select label="Format" value={format}
            onChange={e => setFormat(e.target.value)}
            fullWidth required size="small"
          >
            <MenuItem value={FormatRapport.EXCEL}>Excel</MenuItem>
            <MenuItem value={FormatRapport.PDF}>PDF</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={6} sm={4}>
          <SelectReportTemplate
            value={templateId}
            onChange={id => setTemplateId(id)}
            typeRapport={typeRapport}
            label="Template"
          />
        </Grid>

        {/* ── Filtres ── */}
        <Grid item xs={12}>
          <Divider />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            Filtres
          </Typography>
        </Grid>

        {isDocVente && (
          <Grid item xs={12}>
            <DocumentVenteFilter />
          </Grid>
        )}

        {isStock && (
          <Grid item xs={12}>
            <StockFilters />
          </Grid>
        )}

        {/* Filtres compte tiers (client / fournisseur) */}
        {(typeRapport === TypeRapport.COMPTE_CLIENT) && (
          <Grid item xs={12} sm={6}>
            <ClientAutoC
              onClientChange={tiers => setSelectedTiers(tiers)}
              selectedClient={selectedTiers}
            />
          </Grid>
        )}

        {(typeRapport === TypeRapport.COMPTE_EMPLOYE) && (
          <Grid item xs={12} sm={6}>
            <EmployeAutoC
              onEmployeChange={tiers => setSelectedTiers(tiers)}
              selectedEmploye={selectedTiers}
            />
          </Grid>
        )}

        {(typeRapport === TypeRapport.COMPTE_FOURNISSEUR) && (
          <Grid item xs={12} sm={6}>
            <FournisseurAutoC
              onFournisseurChange={tiers => setSelectedTiers(tiers)}
              selectedFournisseur={selectedTiers}
            />
          </Grid>
        )}

        {/* Filtre caisse */}
        {(typeRapport === TypeRapport.COMPTE_CAISSE) && (
          <Grid item xs={12} sm={6}>
            <SelectCaisse
              onCaisseChange={caisse => setSelectedCaisse(caisse)}
              selectedCaisse={selectedCaisse}
            />
          </Grid>
        )}

        {/* Plage de dates pour tous les types compte */}
        {isCompte && (
          <>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Date début" type="date" value={dateDebut}
                onChange={e => setDateDebut(e.target.value)}
                fullWidth size="small" InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Date fin" type="date" value={dateFin}
                onChange={e => setDateFin(e.target.value)}
                fullWidth size="small" InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </>
        )}

        {/* Plage de dates pour TIERS / LISTE_ARTICLE (sans filtre custom) */}
        {!hasCustomFilter && (
          <>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Date début" type="date" value={dateDebut}
                onChange={e => setDateDebut(e.target.value)}
                fullWidth size="small" InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Date fin" type="date" value={dateFin}
                onChange={e => setDateFin(e.target.value)}
                fullWidth size="small" InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </>
        )}

        {/* Actions */}
        <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="outlined" onClick={onCancel}>Annuler</Button>
          <Button variant="contained" type="submit">Générer</Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportJobForm;
